/**
 * Cliente HTTP das anotações manuais da mesa (E36.F3). // ux-neutrality:allow doc-comment
 *
 * Espelha apps/api/types/mesa_annotation.py e apps/api/runtime/mesa_annotations.py. // ux-neutrality:allow doc-comment
 *
 * Contrato de coordenadas: geometria NORMALIZADA ao espaço da imagem // ux-neutrality:allow doc-comment
 * contestada — x = px/largura, y = px/altura em [0,1]; o raio do círculo é // ux-neutrality:allow doc-comment
 * fração da LARGURA da imagem (eixo único → círculo continua círculo em // ux-neutrality:allow doc-comment
 * qualquer densidade de render). Por viver no espaço da imagem, a anotação // ux-neutrality:allow doc-comment
 * sobrevive a zoom/pan (E36.F2), recarga e captura para o laudo (E36.F4). // ux-neutrality:allow doc-comment
 */
import type { AxiosInstance } from 'axios';

export type AnnotationTool = 'arrow' | 'circle';
export type AnnotationColor = 'green' | 'amber' | 'red';

/**
 * Paleta da mesa — a rampa de sinal do Design System (E54.F5.T01 / ADR-0065),
 * mesmos hex dos marcadores do sistema (ContestedMarkerOverlay) e das sugestões
 * da triagem (PreAnaliseMarkerOverlay).
 *
 * Hex literal, e não `oklch(var(--ok))`, porque estes valores vão para atributo
 * de apresentação de SVG e para o canvas Fabric — nenhum dos dois resolve
 * `var()`. O guard `design_system_telas.test.ts` confere cada um contra o
 * pacote de design versionado (`docs/design/signcheck-ds/`): a correspondência
 * é verificada, não copiada à mão.
 *
 * O nome da chave é a semântica persistida da anotação (o perito escolhe
 * "verde"), então ele não muda quando o valor migra de degrau.
 */
export const ANNOTATION_COLOR_HEX: Record<AnnotationColor, string> = {
  green: '#5f9873',
  amber: '#c79445',
  red: '#c4635f',
};

/** Seta da cauda (x1, y1) à ponta (x2, y2), normalizada à imagem. */
export interface ArrowGeometryData {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** Círculo de centro (cx, cy); `r` é fração da largura da imagem. */
export interface CircleGeometryData {
  cx: number;
  cy: number;
  r: number;
}

export type AnnotationGeometryData = ArrowGeometryData | CircleGeometryData;

export function isArrowGeometry(g: AnnotationGeometryData): g is ArrowGeometryData {
  return 'x1' in g;
}

/**
 * Quem pôs a anotação na mesa (E47.F3, ADR-0058 §2.5.bis). Espelha
 * `AnnotationOrigem` em apps/api/types/mesa_annotation.py.
 *
 * `perito` — desenhada por um humano; entra na captura para o laudo (E36.F4).
 * `triagem` — sugerida pela pré-análise da raia isolada (ADR-0056) e levada à
 * mesa como ponto de partida; desenhada TRACEJADA e mantida FORA da captura até
 * ser adotada. Adotar transfere a autoria e é o único caminho pelo qual esse
 * material passa a poder entrar no documento pericial.
 */
export type AnnotationOrigem = 'perito' | 'triagem';

export interface MesaAnnotationData {
  id: string;
  case_analysis_id: string;
  tool: AnnotationTool;
  color: AnnotationColor;
  geometry: AnnotationGeometryData;
  origem: AnnotationOrigem;
  nota: string | null;
  usuario_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface MesaAnnotationCreateData {
  tool: AnnotationTool;
  color: AnnotationColor;
  geometry: AnnotationGeometryData;
}

export interface MesaAnnotationUpdateData {
  color?: AnnotationColor;
  geometry?: AnnotationGeometryData;
}

function annotationsPath(caseId: string, analysisId: string): string {
  return `/casos/${caseId}/analyses/${analysisId}/annotations`;
}

export async function listMesaAnnotations(
  client: AxiosInstance,
  caseId: string,
  analysisId: string,
): Promise<MesaAnnotationData[]> {
  const { data } = await client.get<MesaAnnotationData[]>(annotationsPath(caseId, analysisId));
  return data;
}

export async function createMesaAnnotation(
  client: AxiosInstance,
  caseId: string,
  analysisId: string,
  payload: MesaAnnotationCreateData,
): Promise<MesaAnnotationData> {
  const { data } = await client.post<MesaAnnotationData>(
    annotationsPath(caseId, analysisId),
    payload,
  );
  return data;
}

export async function updateMesaAnnotation(
  client: AxiosInstance,
  caseId: string,
  analysisId: string,
  annotationId: string,
  payload: MesaAnnotationUpdateData,
): Promise<MesaAnnotationData> {
  const { data } = await client.patch<MesaAnnotationData>(
    `${annotationsPath(caseId, analysisId)}/${annotationId}`,
    payload,
  );
  return data;
}

export async function deleteMesaAnnotation(
  client: AxiosInstance,
  caseId: string,
  analysisId: string,
  annotationId: string,
): Promise<void> {
  await client.delete(`${annotationsPath(caseId, analysisId)}/${annotationId}`);
}

/**
 * Leva as marcações da triagem para a mesa (E47.F3). Idempotente: a mesa chama
 * ao montar e só o primeiro import devolve algo — o que o perito descartou não
 * ressuscita no reload.
 */
export async function importarTriagemAnnotations(
  client: AxiosInstance,
  caseId: string,
  analysisId: string,
): Promise<MesaAnnotationData[]> {
  const { data } = await client.post<MesaAnnotationData[]>(
    `${annotationsPath(caseId, analysisId)}/importar-triagem`,
  );
  return data;
}

/**
 * O perito assume a marcação da triagem como sua. Ato humano deliberado: é o
 * único caminho pelo qual esse material passa a poder entrar no laudo.
 */
export async function adotarMesaAnnotation(
  client: AxiosInstance,
  caseId: string,
  analysisId: string,
  annotationId: string,
): Promise<MesaAnnotationData> {
  const { data } = await client.post<MesaAnnotationData>(
    `${annotationsPath(caseId, analysisId)}/${annotationId}/adotar`,
  );
  return data;
}
